import { ButtonFlavor, ButtonSize, ButtonType, ExButton } from '@boomi/exosphere';
import { At } from '@phosphor-icons/react';
import { type ElementRef, useEffect, useRef, useState } from 'react';
import {
    Mention,
    type MentionItem,
    MentionsInput,
    type OnChangeHandlerFunc,
    type SuggestionDataItem,
} from 'react-mentions';
import { useApp } from '../../common/AppProvider';
import { translations } from '../../translations/translations';
import type { Output } from '../../types/types';

import './input-with-tokens.css';

interface Suggestion extends SuggestionDataItem {
    description?: string;
}

interface InputWithTokensProps {
    id: string;
    tokens: Output[];
    onChange: (newValue: string) => void;
    value: string;
    isSingleLine?: boolean;
}

const InputWithTokens = ({
    id,
    tokens,
    onChange,
    value,
    isSingleLine = false,
}: InputWithTokensProps) => {
    const [mentions, setMentions] = useState<MentionItem[]>([]);
    const inputRef = useRef<ElementRef<'input'>>(null);

    const { language } = useApp();

    // The popup is positioned fixed, so when scrolling it does not move correctly
    // so we just close blur the input to cancel the popup on scroll
    useEffect(() => {
        const handleScroll = () => {
            const inputElement = inputRef.current;

            const suggestionsPopup = document.querySelector('.token-input__suggestions');

            if (suggestionsPopup && inputElement) {
                inputElement.blur();
            }
        };

        const automateContent = document.querySelector('.automate-content');

        automateContent?.addEventListener('scroll', handleScroll);

        return () => {
            automateContent?.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const handleAddToken = () => {
        const inputElement = inputRef.current;
        if (!inputElement) {
            return;
        }

        const plainTextIndex = inputElement.selectionStart ?? 0;

        // Compute the offset between the on-screen (i.e., plain text) cursor location and the corresponding location in the markup value (what we store)
        let offset = 0;
        for (const mention of mentions) {
            // Compute the end index of the mention
            const mentionEnd = mention.plainTextIndex + mention.display.length;
            // For each complete mention in the plain text value before the cursor location, add an offset of two, one for each bracket character in a mention's markup
            if (mentionEnd <= plainTextIndex) {
                offset += 2;
            }
        }
        const markupIndex = plainTextIndex + offset;

        // Get the text before and after the cursor in the markup value
        const textBefore = value.slice(0, markupIndex);
        const textAfter = value.slice(markupIndex, value.length);
        // Prepend a space unless we're at the beginning of the input or the last character is whitespace or the closing tag of a token; needed for popup to appear
        const lastChar = value[markupIndex - 1];
        const textToInsert = !lastChar || /\s/.test(lastChar) || /}/.test(lastChar) ? '@' : ' @';

        // Insert the @ character into the markup value at the location of the cursor
        const newMarkupValue = `${textBefore}${textToInsert}${textAfter}`;
        onChange(newMarkupValue);

        // Required; cursor sometimes appears in wrong place without using requestAnimationFrame
        requestAnimationFrame(() => {
            const newPlainTextIndex = plainTextIndex + textToInsert.length;
            inputElement.setSelectionRange(newPlainTextIndex, newPlainTextIndex);

            const keydownEvent = new KeyboardEvent('keydown', {
                key: '',
                bubbles: true,
            });

            // Dispatch an empty keyboard event as the modal only opens when typing
            inputElement.dispatchEvent(keydownEvent);
        });

        inputElement.focus();
    };

    const handleChange: OnChangeHandlerFunc = (_event, markupValue, _plainTextValue, mentions) => {
        onChange(markupValue);
        setMentions(mentions);
    };

    return (
        <div className="token-input-container faux-ex-input">
            <MentionsInput
                id={id}
                data-testid={id}
                className="token-input"
                inputRef={inputRef}
                value={value}
                onChange={handleChange}
                singleLine={isSingleLine}
                allowSuggestionsAboveCursor
                onPaste={(e) => {
                    const pastedText = e.clipboardData.getData('text/plain');

                    if (pastedText.includes('\n')) {
                        e.stopPropagation();

                        const cleanedText = pastedText.replaceAll('\n', '').replaceAll('\r', ''); // Remove all new lines

                        // Create a new clipboard data object with the cleaned text
                        const newClipboardData = new DataTransfer();
                        newClipboardData.setData('text/plain', cleanedText);

                        // Dispatch a new paste event with the cleaned clipboard data
                        inputRef.current?.dispatchEvent(
                            new ClipboardEvent('paste', { clipboardData: newClipboardData }),
                        );
                    }
                }}
            >
                <Mention
                    trigger="@"
                    data={(search) => {
                        const filteredTokens = tokens.filter(
                            (token) =>
                                token.name.toLowerCase().includes(search.toLowerCase()) ||
                                token.description.toLowerCase().includes(search.toLowerCase()),
                        );

                        return filteredTokens.map((token) => ({
                            id: token.name,
                            display: token.name,
                            description: token.description,
                        }));
                    }}
                    markup="{__id__}"
                    regex={new RegExp(`{(${tokens.map((token) => token.name).join('|')})}`)}
                    renderSuggestion={(suggestion: Suggestion) => (
                        <div className="token-input-wrapper">
                            <p className="label sm">{suggestion.id}</p>
                            <p className="token-input-description text xs">
                                {suggestion.description === '' ? '---' : suggestion.description}
                            </p>
                        </div>
                    )}
                />
            </MentionsInput>
            {tokens.length > 0 && (
                <ExButton
                    className="add-token-button"
                    flavor={ButtonFlavor.BASE}
                    size={ButtonSize.DEFAULT}
                    type={ButtonType.TERTIARY}
                    onClick={handleAddToken}
                    aria-label={translations[language].TASK_add_output_button_label}
                    title={translations[language].TASK_add_output_button_label}
                >
                    {/* No equivalent ExIcon so keeping Phosphor */}
                    <At size={24} />
                </ExButton>
            )}
        </div>
    );
};

export default InputWithTokens;
